import React from 'react';
import { useTranslation } from 'react-i18next';
import { defaultTruckCompanyDetail, defaultWoodImportTransactionDetail, defaultWoodSizeGradeMapDetail, WOOD_IMPORT_GRADE_DISPLAY } from '../../constance/dataTypes';
import propTypes from 'prop-types';
import useSearchItem from '../../hooks/useSeatchItem';
import useDropdownItem from '../../hooks/useDropdownItem';
import { URL_BARCODE, URL_SCALE_LARGE } from '../../constance/urls';
import { useDebounce } from 'use-debounce';
import useItemDetail from '../../hooks/useItemDetail';
import { formatComma } from '../../utils/stringFormat';
import * as _ from 'lodash';
import SelectTruckTransactionModal from '../truck-transaction/SelectTruckTransactionModal';
import CreateTruckTransactionModal from '../truck-transaction/CreateTruckTransactionModal';
import { Alert, Form, Space, Input, Button, AutoComplete, Select, Col, Row, Card, InputNumber, DatePicker, Layout, Typography } from 'antd';
import { ProfileFilled, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { DATE_FORMAT, HUMAN_DATE_FORMAT } from '../../constance/strings';
import dayjs from 'dayjs';
import { BLUE_COLOR } from '../../constance/color';
import ColorButton from '../common/ColorButton';

const WoodImportDetail = React.forwardRef((props, ref) => {

  const {
    detail,
    enableEdit,
    setDetailLoading
  } = props;

  const { Text } = Typography;

  const { t } = useTranslation();

  const [truckCompanySearchTxt, setTruckCompanySearchTxt] = React.useState("")
  const [debouncedText] = useDebounce(truckCompanySearchTxt, 500);
  const [form] = Form.useForm();
  const [itemTotals, setItemTotals] = React.useState({});

  const sawWoodTypes = useDropdownItem({ url: URL_BARCODE.SAW_WOOD_TYPE });
  const truckTypes = useDropdownItem({ url: URL_SCALE_LARGE.TRUCK_TYPE, text: "name" });
  const truckCompanies = useSearchItem({ url: URL_SCALE_LARGE.TRUCK_COMPANY, description_render: 'name', title_render: 'code' });

  const woodImportTransactionDetail = useItemDetail({
    url: URL_BARCODE.WOOD_IMPORT_TRANSACTION,
    id: detail ? detail.id : null,
    manual: true,
    returnData: true,
  })

  const date = Form.useWatch('produce_date', form)
  const [isSelectTruckTransactionOpen, setIsSelectTruckTransactionOpen] = React.useState(false)
  const [isCreateTruckTransactionOpen, setIsCreateTruckTransactionOpen] = React.useState(false)
  const [defaultSawWoodType, setDefaultSawWoodType] = React.useState(null)
  const truck_transaction_list = form.getFieldValue('truck_transaction_list') || [];
  const [values, setValues] = React.useState({
    waste: 0,
    over: 0,
    grades: {},
  });

  const updateWoodSizeGradeMapField = (field, value) => {
    setValues(prevValues => {
      const newValues = { ...prevValues, [field]: value || 0 };
      return newValues;
    });
  };

  const updateValue = (index, fieldName, newover) => {
    if (newover === null || newover === "") {
      newover = 0;
    }
    const updatedData = [...form.getFieldValue('wood_size_grade_map_list')];
    updatedData[index][fieldName] = newover;

    form.setFieldsValue({
      wood_size_grade_map_list: updatedData,
    });
  };
  const addNewGradeMap = () => {
    const newGradeMap = {
      ...defaultWoodSizeGradeMapDetail,
      saw_wood_type: defaultSawWoodType
    };

    form.setFieldValue('wood_size_grade_map_list', [
      ...form.getFieldValue('wood_size_grade_map_list'),
      newGradeMap
    ]);
  }

  const addTruckTransactionList = (params) => {
    const inputData = {
      truck_company: params.truck_company,
      truck_company_name: `[${params.truck_company}] - ${params.truck_company_name}`,
      truck_type: params.truck_type,
      truck_type_name: params.truck_type_name,
      truck_license_plate: params.license_plate,
      in_weight: Math.round(params.net_weight),
    }
    const target = truck_transaction_list.findIndex(
      tt => tt.exported_truck_license_plate && tt.exported_truck_license_plate == params.license_plate
    )

    if (target != -1) {
      let newList = truck_transaction_list.map((tt, index) => index == target ? ({ ...tt, ...inputData }) : tt)
      form.setFieldValue('truck_transaction_list', [...newList])
    } else {
      form.setFieldValue('truck_transaction_list', [...truck_transaction_list, inputData])
    }
  }

  const updateTruckTransactionList = (form, idx, updatedFields) => {
    const updatedValues = truck_transaction_list.map((field, index) =>
      index === idx
        ? { ...field, ...updatedFields }
        : field
    );
    form.setFieldsValue({ truck_transaction_list: updatedValues });
  };

  React.useImperativeHandle(ref, () => ({
    handleSaveDetail: async () => {
      try {
        const values = await form.validateFields();

        let isCreate = !detail?.id;
        let returnData = defaultWoodImportTransactionDetail;
        let isSuccess = false;

        if (isCreate) {
          let [createResponse, isCreateSuccess] = await woodImportTransactionDetail.handleCreate(values);
          returnData = { ...returnData, ...createResponse };
          isSuccess = isCreateSuccess;
        } else {
          let [saveResponse, isSaveSuccess] = await woodImportTransactionDetail.handleSave(values);
          returnData = { ...returnData, ...saveResponse };
          isSuccess = isSaveSuccess;
        }
        return [isCreate, isSuccess, returnData];

      } catch (error) {
        console.error('Validation failed:', error);
        return [false, false, error];
      }
    },
  }));

  React.useEffect(() => {
    truckCompanies.fetchData()
  }, [])

  React.useEffect(() => {
    if (debouncedText) {
      truckCompanies.fetchData({ search: debouncedText })
    }
  }, [debouncedText])

  React.useEffect(() => {
    const targetSawWoodType = sawWoodTypes.response.find(e => e.code == '11')
    setDefaultSawWoodType(_.get(targetSawWoodType, 'id', null))
    if (!detail.id) {
      form.setFieldValue('wood_size_grade_map_list', [
        { ...defaultWoodSizeGradeMapDetail, saw_wood_type: _.get(targetSawWoodType, 'id', null) }
      ])
    }
  }, [sawWoodTypes.response])

  React.useEffect(() => {
    if (detail) {
      const formattedList = detail.truck_transaction_list.map((transaction) => ({
        ...transaction,
        truck_company_name: `[${transaction.truck_company}] - ${transaction.truck_company_name}`,
        in_weight: Math.round(transaction.in_weight),
        exported_truck_license_plate: transaction.exported_truck_license_plate ? transaction.exported_truck_license_plate : '-',
        exported_net_weight: Math.round(transaction.exported_net_weight),
      }));
      form.setFieldsValue({
        ...detail,
        produce_date: detail.produce_date ? dayjs(detail.produce_date) : null,
        truck_transaction_list: formattedList,
      });
    } else {
      form.setFieldsValue({
        ...defaultWoodImportTransactionDetail,
        produce_date: null
      });
    }
  }, [detail]);

  React.useEffect(() => {
    const woodSizeGradeList = form.getFieldValue('wood_size_grade_map_list');
    if (woodSizeGradeList) {
      const newItemTotals = woodSizeGradeList.map(item => {
        const gradeTotal = WOOD_IMPORT_GRADE_DISPLAY.reduce(
          (sum, grade) => sum + Number(item[grade] || 0),
          0
        );
        return gradeTotal;
      });
      setItemTotals(newItemTotals);
    }
  }, [form.getFieldValue('wood_size_grade_map_list')]);

  return (
    <div>
      <Layout>
        <Card>
          {woodImportTransactionDetail.errorMessages &&
            <Alert
              message={t('error')}
              description={woodImportTransactionDetail.errorMessages}
              type='error'
            />
          }
          <Form form={form} initialValues={detail || defaultWoodImportTransactionDetail} layout='vertical' >
            <Row gutter={[16, 16]}>
              <Col sm={24} md={24} lg={6} style={{ width: '100%' }} >
                <Form.Item
                  label={t('wood_imports.order_no')}
                  name={'order_no'}
                  rules={[{ required: true, message: t('error_messages.validation.required') }]}
                ><Input readOnly={!enableEdit} />
                </Form.Item>
              </Col>
              <Col sm={24} md={24} lg={6} style={{ width: '100%' }}>
                <Form.Item
                  label={t('wood_imports.produce_date')}
                  name={'produce_date'}
                  rules={[{ required: true, message: t('error_messages.validation.required') }]}>
                  <DatePicker
                    format={HUMAN_DATE_FORMAT}
                    placeholder={t('searchs.placeholders.date')}
                    style={{ width: '100%' }}
                    inputReadOnly={!enableEdit}
                    open={enableEdit ? undefined : false}
                    allowClear={enableEdit ? undefined : false}
                  />
                </Form.Item>

              </Col>
              {enableEdit &&
                <Col sm={24} md={24} lg={6} style={{ width: '100%' }}>
                  <div style={{ paddingTop: '29px', }}>
                    <ColorButton
                      icon={<ProfileFilled />}
                      color='primary'
                      variant='outlined'
                      override={BLUE_COLOR}
                      onClick={() => setIsSelectTruckTransactionOpen(true)}
                      block
                    >{t('wood_imports.map_from_truck_transaction')}</ColorButton>
                  </div>
                </Col>
              }
              {enableEdit &&
                <Col sm={24} md={24} lg={5} style={{ width: '100%' }}>
                  <div style={{ paddingTop: '29px' }}>
                    <Button
                      icon={<PlusOutlined />}
                      variant='outlined'
                      color='primary'
                      onClick={() => setIsCreateTruckTransactionOpen(true)}
                      block>
                      {t('wood_imports.map_from_manual')}</Button>
                  </div>
                </Col>
              }
            </Row>

            <Form form={form} initialValues={{ truck_transaction_list: [], }} layout='vertical'>
              <Form.List name="truck_transaction_list" >
                {(fields, { add, remove }) => (
                  <><Space direction='vertical' size="middle">
                    {fields.map(({ key, name, ...restField }, idx) => (
                      <Row gutter={[16, 16]}>
                        <Col sm={24} md={24} lg={6} style={{ width: '100%' }}>
                          <Form.Item name={[name, "truck_company"]} hidden />
                          <Form.Item
                            {...restField}
                            label={t('truck_transactions.company')}
                            name={[name, 'truck_company_name']}
                            rules={[{ required: true, message: t('error_messages.validation.required') }]}>
                            <AutoComplete
                              options={truckCompanies.response.map((company) => ({
                                ...company,
                                value: `[${company.code}] - ${company.name}`,
                                label: (
                                  <Space direction="vertical" size={1}>
                                    <Text strong>{company.code}</Text>
                                    <Text type="secondary" >{company.name}</Text>
                                  </Space>
                                ),
                              }))
                              }
                              onSearch={(value) => {
                                setTruckCompanySearchTxt(value);
                              }}
                              onSelect={(value, data) => {
                                updateTruckTransactionList(form, idx, {
                                  truck_company: data.code,
                                  truck_company_name: value,
                                });
                              }}
                              open={enableEdit ? undefined : false}
                              notFoundContent={truckCompanySearchTxt ? t('no_data') : ""}
                            >
                              <Input.Search
                                placeholder={`${t('search')} ${t('searchs.fields.code')} ${t('or')} ${t('searchs.fields.name')}...`}
                                loading={truckCompanies.isLoading}
                                onFocus={(e) => {
                                  if (!enableEdit) e.target.blur();
                                }} />
                            </AutoComplete>
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={24} lg={3} style={{ width: '100%' }}>
                          <Form.Item
                            {...restField}
                            label={t('wood_imports.truck_license_plate')}
                            name={[name, 'truck_license_plate']}
                            rules={[{ required: true, message: t('error_messages.validation.required') }]}
                          >
                            <Input readOnly={!enableEdit} />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={24} lg={3} style={{ width: '100%' }}>
                          <Form.Item
                            {...restField}
                            label={t('trucks.type')}
                            name={[name, 'truck_type']}
                            rules={[{ required: true, message: t('error_messages.validation.key_not_found') }]}
                          >
                            <Select
                              showSearch={enableEdit ? true : false}
                              options={truckTypes.response}
                              filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                              }
                              open={enableEdit ? undefined : false}
                              allowClear={enableEdit ? true : false}
                            />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={24} lg={3} style={{ width: '100%' }}>
                          <Form.Item
                            {...restField}
                            label={t('wood_imports.in_weight')}
                            name={[name, 'in_weight']}
                            rules={[{ required: true, message: t('error_messages.validation.required') },]}>
                            <Input
                              suffix={t('units.kg')}
                              labelPosition='right'
                              type='number'
                              readOnly={!enableEdit}
                              onChange={(e) => {
                                form.setFieldValue(['truck_transaction_list', idx, 'in_weight'],
                                  Math.abs(e.target.value))
                              }}
                            />
                          </Form.Item>
                        </Col>

                        {enableEdit &&
                          <Col sm={24} md={24} lg={2} style={{ width: '100%' }}>
                            <div style={{ paddingLeft: '24px' }}>
                              <Form.Item
                                {...restField} label='Action'>
                                <Button
                                  variant='outlined'
                                  color='danger'
                                  icon={<CloseOutlined />}
                                  onClick={() => remove(idx)}
                                />
                              </Form.Item>
                            </div>
                          </Col>
                        }

                        {/* issued it as input as intended*/}
                        <Col sm={24} md={24} lg={3} style={{ width: '100%' }}>
                          <Form.Item
                            {...restField}
                            label={t('wood_imports.truck_license_plate_out')}
                            name={[name, 'exported_truck_license_plate']}
                          >
                            <Input readOnly />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={24} lg={4} style={{ width: '100%' }}>
                          <Form.Item
                            {...restField}
                            label={t('wood_imports.exported_net_weight')}
                            name={[name, 'exported_net_weight']}
                          >
                            <Input
                              suffix={t('units.kg')} readOnly />
                          </Form.Item>
                        </Col>
                      </Row>
                    ))}
                  </Space>
                  </>
                )}

              </Form.List>
              <br />
            </Form>

            <label style={{ fontWeight: 'bold' }}>{t('wood_imports.wood_size_grade_map_list')}</label>
            <Form form={form} initialValues={{ wood_size_grade_map_list: [], }} layout='vertical'>
              <Form.List name="wood_size_grade_map_list" >
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <Card style={{ borderBlockStartColor: 'blue', marginTop: '24px' }}>
                        {enableEdit &&
                          <div style={{ textAlign: 'right' }}>
                            <Button
                              icon={<CloseOutlined />}
                              size='small'
                              variant='outlined'
                              color='danger'
                              onClick={() => remove(index)} />
                          </div>
                        }
                        <Row gutter={[16, 16]}>
                          <Col sm={24} md={24} lg={8} style={{ width: '100%' }}>
                            <Form.Item
                              {...restField}
                              label={t('wood_imports.wood_code')}
                              name={[name, 'wood_code']}
                              rules={[{ required: true, message: t('error_messages.validation.required') },
                              { len: 12, message: `${t('error_messages.validation.length')} (12 ${t('units.character')})` }
                              ]}>
                              <Input readOnly={!enableEdit} maxLength={12} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={24} lg={8} style={{ width: '100%' }}>
                            <Form.Item
                              {...restField}
                              label={t('wood_imports.saw_wood_type')}
                              name={[name, 'saw_wood_type']}>
                              <Select
                                showSearch={enableEdit ? undefined : false}
                                options={sawWoodTypes.response}
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                open={enableEdit ? undefined : false}
                                allowClear={enableEdit ? true : false}
                              />
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={24} lg={8} style={{ width: '100%' }}>
                            <Form.Item
                              {...restField}
                              label={t('wood_imports.barcode_ref')}
                              name={[name, 'barcode_ref']}
                              rules={[{ required: true, message: t('error_messages.validation.required') }]}>
                              <Input readOnly={!enableEdit} />
                            </Form.Item>
                          </Col>
                        </Row>
                        <br />

                        <Row gutter={[3, 16]}>
                          {WOOD_IMPORT_GRADE_DISPLAY.map(grade => (
                            <Col sm={24} md={24} lg={3} style={{ width: '100%' }}>
                              <Form.Item
                                {...restField}
                                label={t(`wood_imports.grades.${grade}`)}
                                key={grade}
                                name={[name, grade]}
                                initialValue={0}
                              >
                                <InputNumber
                                  min={0}
                                  readOnly={!enableEdit}
                                  onChange={(value) => updateWoodSizeGradeMapField(`grades.${grade}`, value)}
                                  style={{ width: '100%' }}
                                  onBlur={(e) => updateValue(index, grade, e.target.value)}
                                />
                              </Form.Item>
                            </Col>
                          ))}
                          <Col sm={24} md={24} lg={3} style={{ width: '100%' }}>
                            <Form.Item
                              {...restField}
                              label={t('wood_imports.grades.waste')}
                              name={[name, 'waste']}
                              key={index}>
                              <InputNumber readOnly={!enableEdit} onChange={(value) => updateWoodSizeGradeMapField('waste', value)}
                                style={{ width: '100%' }} min={0}
                                onBlur={(e) => updateValue(index, 'waste', e.target.value)} />
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={24} lg={3} style={{ width: '100%' }}>
                            <Form.Item
                              {...restField}
                              label={t('wood_imports.grades.over')}
                              name={[name, 'over']}
                              key={index} >
                              <InputNumber readOnly={!enableEdit} onChange={(value) => updateWoodSizeGradeMapField('over', value)}
                                style={{ width: '100%' }} min={0}
                                onBlur={(e) => updateValue(index, 'over', e.target.value)} />
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={24} lg={3} style={{ width: '100%' }}>
                            <Form.Item label={t('datas.total')}>
                              <Input
                                value={`${formatComma(itemTotals[index] || 0)} ${t('units.wood')}`} variant="borderless" readOnly />
                            </Form.Item>
                          </Col>
                        </Row>


                      </Card>
                    ))}
                  </>
                )}
              </Form.List>
            </Form>

            <br />
            <br />

          </Form >

          {
            enableEdit &&
            <Row justify="center">
              <Col sm={24} md={24} lg={8} style={{ width: '100%' }} >
                <Form form={form} initialValues={{ wood_size_grade_map_list: [] }}>
                  <Button
                    block
                    variant='outlined'
                    color='primary'
                    onClick={addNewGradeMap}
                  ><PlusOutlined /></Button>
                </Form>
              </Col>
            </Row>
          }

          <SelectTruckTransactionModal
            open={isSelectTruckTransactionOpen}
            onSelect={(tt) => addTruckTransactionList(tt)}
            onClose={() => setIsSelectTruckTransactionOpen(false)}

          />
          <CreateTruckTransactionModal
            open={isCreateTruckTransactionOpen}
            onClose={() => setIsCreateTruckTransactionOpen(false)}
            onFinish={(tt) => addTruckTransactionList(tt)} />
        </Card >
      </Layout>
    </div>

  )

});

export default WoodImportDetail;

WoodImportDetail.defaultProps = {
  detail: defaultWoodImportTransactionDetail,
  enableEdit: true,
  setDetailLoading: () => null,
};

WoodImportDetail.propTypes = {
  detail: propTypes.object,
  enableEdit: propTypes.bool,
  setDetailLoading: propTypes.func,
};
